import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";
import { queryOperatorsProtected } from "../lib/operatorsApi";
import { useNetworkingStore } from "../stores/networkingStore";
import { useAuthStore } from "../stores/authStore";
import type { Collaboration, Notification, Operator } from "../types/database";

// ============================================
// COLLABORATIONS
// ============================================

export function useCollaborations() {
  const { user } = useAuthStore();
  const { setCollaborations } = useNetworkingStore();

  return useQuery({
    queryKey: ["collaborations", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from("collaborations")
        .select(`
          *,
          target_operator:operators(*)
        `)
        .eq("requester_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;

      const collaborations = data as Collaboration[];
      setCollaborations(collaborations);
      return collaborations;
    },
    enabled: !!user?.id,
  });
}

export function useCreateCollaboration() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const { addCollaboration } = useNetworkingStore();

  return useMutation({
    mutationFn: async ({
      operatorId,
      message
    }: {
      operatorId: string;
      message?: string;
    }) => {
      if (!user?.id) throw new Error("User not authenticated");

      const { data, error } = await supabase
        .from("collaborations")
        .insert({
          requester_id: user.id,
          target_operator_id: operatorId,
          message,
          status: "pending",
        })
        .select(`
          *,
          target_operator:operators(*)
        `)
        .single();

      if (error) throw error;
      return data as Collaboration;
    },
    onSuccess: (data) => {
      addCollaboration(data);
      queryClient.invalidateQueries({ queryKey: ["collaborations"] });
    },
  });
}

export function useUpdateCollaboration() {
  const queryClient = useQueryClient();
  const { updateCollaboration } = useNetworkingStore();

  return useMutation({
    mutationFn: async ({
      id,
      status,
      responseMessage
    }: {
      id: string;
      status: Collaboration["status"];
      responseMessage?: string;
    }) => {
      const { data, error } = await supabase
        .from("collaborations")
        .update({
          status,
          response_message: responseMessage,
          responded_at: new Date().toISOString(),
        })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data as Collaboration;
    },
    onSuccess: (data) => {
      updateCollaboration(data.id, data);
      queryClient.invalidateQueries({ queryKey: ["collaborations"] });
    },
  });
}

export function useCancelCollaboration() {
  const queryClient = useQueryClient();
  const { removeCollaboration } = useNetworkingStore();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("collaborations")
        .delete()
        .eq("id", id);

      if (error) throw error;
      return id;
    },
    onSuccess: (id) => {
      removeCollaboration(id);
      queryClient.invalidateQueries({ queryKey: ["collaborations"] });
    },
  });
}

// ============================================
// NOTIFICATIONS
// ============================================

export function useNotifications() {
  const { user } = useAuthStore();
  const { setNotifications } = useNetworkingStore();

  return useQuery({
    queryKey: ["notifications", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from("notifications")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(50);

      if (error) throw error;

      const notifications = data as Notification[];
      setNotifications(notifications);
      return notifications;
    },
    enabled: !!user?.id,
    refetchInterval: 30000, // Refresh every 30 seconds
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  const { markAsRead } = useNetworkingStore();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("notifications")
        .update({ is_read: true })
        .eq("id", id);

      if (error) throw error;
      return id;
    },
    onSuccess: (id) => {
      markAsRead(id);
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const { markAllAsRead } = useNetworkingStore();

  return useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error("User not authenticated");

      const { error } = await supabase
        .from("notifications")
        .update({ is_read: true })
        .eq("user_id", user.id)
        .eq("is_read", false);

      if (error) throw error;
    },
    onSuccess: () => {
      markAllAsRead();
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

// ============================================
// SUGGESTED OPERATORS (paginated with infinite query)
// ============================================

const OPERATORS_PAGE_SIZE = 50;

export function useSuggestedOperators(region?: string) {
  const { user, token } = useAuthStore();

  return useInfiniteQuery({
    queryKey: ["suggested-operators", region],
    queryFn: async ({ pageParam = 0 }) => {
      if (!token) {
        throw new Error("Not authenticated");
      }

      const offset = pageParam * OPERATORS_PAGE_SIZE;
      const limit = OPERATORS_PAGE_SIZE;

      // Use the same edge function as the map for consistency
      const operators = await queryOperatorsProtected<Operator[]>(
        token,
        "search_all",
        {
          offset,
          limit,
          region: region || null,
          categories: null,
          types: null,
          dateStart: null,
          dateEnd: null,
        }
      );

      // For total count, we need to make a separate call with limit 1 to get the count
      // Or we can estimate based on whether we got a full page
      const hasMore = operators.length === OPERATORS_PAGE_SIZE;

      return {
        data: operators || [],
        nextPage: hasMore ? pageParam + 1 : undefined,
        totalCount: hasMore ? undefined : (pageParam * OPERATORS_PAGE_SIZE + operators.length), // Approximate
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    initialPageParam: 0,
    enabled: !!user?.id && !!token,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

